import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template9Props {
  data: ResumeData;
}

export const Template9: React.FC<Template9Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-serif">
      {/* Classic Header */}
      <header className="text-center py-8 mb-8 border-b-2 border-gray-800">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">{data.personalInfo.name}</h1>
        <h2 className="text-lg text-gray-700 italic mb-6">{data.personalInfo.title}</h2>
        
        <div className="text-sm text-gray-600 space-x-4">
          <span>{data.personalInfo.email}</span>
          <span>•</span>
          <span>{data.personalInfo.phone}</span>
          <span>•</span>
          <span>{data.personalInfo.location}</span>
          {data.personalInfo.linkedin && (
            <>
              <span>•</span>
              <span>{data.personalInfo.linkedin}</span>
            </>
          )}
          {data.personalInfo.website && (
            <>
              <span>•</span>
              <span>{data.personalInfo.website}</span>
            </>
          )}
        </div>
      </header>

      {/* Professional Summary */}
      <section className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
          ─────────   PROFESSIONAL SUMMARY   ─────────
        </h3>
        <p className="text-gray-700 leading-relaxed text-center italic max-w-3xl mx-auto">
          &ldquo;{data.summary}&rdquo;
        </p>
      </section>

      {/* Professional Experience */}
      <section className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">
          ─────────   PROFESSIONAL EXPERIENCE   ─────────
        </h3>
        
        {data.experience.map((job, index) => (
          <div key={index} className="mb-8">
            <div className="text-center mb-4">
              <h4 className="text-xl font-bold text-gray-900">{job.title}</h4>
              <p className="text-lg text-gray-700 italic">{job.company}</p>
              <p className="text-sm text-gray-600">{job.location} • {job.dates}</p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <ul className="space-y-3">
                {job.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start">
                    <span className="text-gray-400 mr-4 mt-1 text-lg">❋</span>
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {index < data.experience.length - 1 && (
              <div className="text-center my-6">
                <span className="text-gray-400 text-lg">⬥ ⬥ ⬥</span>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Education & Skills */}
      <div className="grid grid-cols-2 gap-12 mb-8">
        {/* Education */}
        <section>
          <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">
            ──── EDUCATION ────
          </h3>
          
          <EducationFormatter 
            education={data.education} 
            className="text-gray-900 text-center"
          />
        </section>

        {/* Skills */}
        <section>
          <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">
            ──── EXPERTISE ────
          </h3>
          
          <div className="space-y-2">
            {data.skills.map((skill, index) => (
              <div key={index} className="text-center">
                <span className="text-gray-700 border-b border-dotted border-gray-400 pb-1">
                  {skill}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Footer decoration */}
      <footer className="text-center pt-6 border-t border-gray-300">
        <div className="text-gray-400 text-lg">❈ ❈ ❈</div>
      </footer>
    </div>
  );
};